package com.writerslab

import io.ktor.client.HttpClient
import io.ktor.client.engine.js.Js
import io.ktor.client.request.forms.FormDataContent
import io.ktor.client.request.post
import io.ktor.client.request.setBody
import io.ktor.client.statement.bodyAsText
import io.ktor.http.Parameters
import kotlinx.browser.window
import kotlinx.serialization.json.Json
import kotlinx.serialization.json.contentOrNull
import kotlinx.serialization.json.jsonObject
import kotlinx.serialization.json.jsonPrimitive
import org.w3c.dom.url.URLSearchParams

/**
 * OAuth2 / OIDC integration with Keycloak. Token endpoint is hit directly
 * from the browser (the realm client is public, no client_secret needed) —
 * no backend proxy. Tokens live in localStorage via AppDataStore.
 */
object Auth {
    private const val CLIENT_ID = "writerslab-frontend"

    private val keycloakBase: String
        get() = (window.asDynamic()._wlKeycloakBase as? String)
            ?: "http://localhost:8180/realms/writerslab"

    private val redirectUri: String
        get() = window.location.origin + "/"

    /** Bare HttpClient for token exchange — must NOT go through the auth-aware
     *  client in ApiClient.kt (would be a refresh recursion trap). Forces
     *  `expectSuccess = false` so 4xx responses (e.g. Keycloak's 400 for
     *  `invalid_grant`) come back as a regular response we can parse,
     *  not as a thrown exception that would short-circuit our outcome
     *  classifier and leave the user logged in on a dead refresh token. */
    private val tokenClient = HttpClient(Js) {
        expectSuccess = false
    }

    val isLoggedIn: Boolean get() = AppDataStore.getAccessToken() != null
    val accessToken: String? get() = AppDataStore.getAccessToken()

    fun saveState(bookId: String?, fileName: String?, analysisJson: String?) {
        if (bookId != null && fileName != null && analysisJson != null) {
            AppDataStore.saveAnalysisState(bookId, fileName, analysisJson)
        }
    }

    fun restoreBookId(): String? = AppDataStore.getSavedBookId()
    fun restoreFileName(): String? = AppDataStore.getSavedFileName()
    fun restoreAnalysis(): String? = AppDataStore.getSavedAnalysisJson()
    fun clearSavedState() = AppDataStore.clearAnalysisState()

    fun login() {
        val authUrl = "$keycloakBase/protocol/openid-connect/auth" +
                "?client_id=$CLIENT_ID" +
                "&response_type=code" +
                "&redirect_uri=${encodeURIComponent(redirectUri)}" +
                "&scope=openid%20email%20profile%20offline_access"
        window.location.href = authUrl
    }

    fun logout() {
        val idToken = AppDataStore.getIdToken()
        AppDataStore.clearTokens()
        AppDataStore.clearEditDraft()
        val logoutUrl = "$keycloakBase/protocol/openid-connect/logout" +
                "?id_token_hint=${idToken ?: ""}" +
                "&post_logout_redirect_uri=${encodeURIComponent(redirectUri)}"
        window.location.href = logoutUrl
    }

    suspend fun handleCallback(): Boolean {
        val code = urlParam("code") ?: return false

        val payload = tokenClient.post("$keycloakBase/protocol/openid-connect/token") {
            setBody(FormDataContent(Parameters.build {
                append("grant_type", "authorization_code")
                append("client_id", CLIENT_ID)
                append("code", code)
                append("redirect_uri", redirectUri)
            }))
        }.bodyAsText()
        val obj = refreshResponseJson.parseToJsonElement(payload).jsonObject
        val token = obj["access_token"]?.jsonPrimitive?.contentOrNull
        val idToken = obj["id_token"]?.jsonPrimitive?.contentOrNull
        val refreshToken = obj["refresh_token"]?.jsonPrimitive?.contentOrNull
        if (token != null) {
            AppDataStore.saveTokens(token, idToken, refreshToken)
            // Invalidate any cached server-side user id from a previous
            // session so a different user signing in on the same browser
            // doesn't briefly see the previous user's UserCache snapshot.
            // `loadUserInfo` re-populates this from the next /me response.
            AppDataStore.clearUserId()
            // Drop the Bearer plugin's cached null tokens so the very first
            // post-login request reads the freshly stored access token.
            resetAuthCache()
            // Strip ?code=... from the URL so a reload doesn't replay it.
            window.history.replaceState(emptyJsObject(), "", window.location.pathname)
            return true
        }
        return false
    }

    /**
     * Exchange the stored refresh token for a fresh access token directly with
     * Keycloak's token endpoint. Returns the new access token, or null if
     * refresh failed.
     *
     * Tokens are wiped ONLY when Keycloak explicitly says the refresh token
     * is no longer usable (`error == "invalid_grant"`). Everything else —
     * network failures, 5xx maintenance pages, malformed JSON, transient
     * `temporarily_unavailable` / `server_error` — leaves the stored tokens
     * intact so the next request can retry. The previous version cleared
     * tokens on every failure, which logged users out whenever the mobile
     * connection blipped or Keycloak briefly returned an HTML error page.
     */
    suspend fun refreshAccessToken(): String? {
        val refresh = AppDataStore.getRefreshToken() ?: return null

        val payload = try {
            tokenClient.post("$keycloakBase/protocol/openid-connect/token") {
                setBody(FormDataContent(Parameters.build {
                    append("grant_type", "refresh_token")
                    append("client_id", CLIENT_ID)
                    append("refresh_token", refresh)
                }))
            }.bodyAsText()
        } catch (_: Throwable) {
            // Network error / TLS reset / browser killed the request in a
            // background tab. Don't touch tokens — next request will retry.
            return null
        }

        return when (val outcome = parseRefreshResponse(payload)) {
            is RefreshOutcome.Success -> {
                AppDataStore.updateAccessToken(outcome.accessToken, outcome.refreshToken)
                outcome.accessToken
            }
            RefreshOutcome.InvalidGrant -> {
                AppDataStore.clearTokens()
                null
            }
            RefreshOutcome.TransientFailure -> null
        }
    }
}

/**
 * Outcome of parsing a Keycloak `/token` response. Split out so the response
 * handling can be unit-tested without standing up a real HttpClient.
 */
internal sealed class RefreshOutcome {
    /** Server returned a usable access token. [refreshToken] may be null if
     *  Keycloak chose not to rotate (rotation is on by default). */
    data class Success(val accessToken: String, val refreshToken: String?) : RefreshOutcome()
    /** Server explicitly said the refresh token is no longer usable. Caller
     *  should drop stored tokens — the user must log in again. */
    object InvalidGrant : RefreshOutcome()
    /** Anything else: network blip, 5xx, HTML maintenance page, malformed
     *  JSON, `temporarily_unavailable` / `server_error`. Caller MUST keep
     *  the stored refresh token — the next attempt may well succeed. */
    object TransientFailure : RefreshOutcome()
}

private val refreshResponseJson = Json { ignoreUnknownKeys = true }

/**
 * Pure parser for a token-endpoint response. No I/O, no storage access —
 * just turn a response body into a decision.
 */
internal fun parseRefreshResponse(payload: String): RefreshOutcome {
    val obj = try {
        refreshResponseJson.parseToJsonElement(payload).jsonObject
    } catch (_: Throwable) {
        return RefreshOutcome.TransientFailure
    }
    val access = obj["access_token"]?.jsonPrimitive?.contentOrNull
    if (!access.isNullOrEmpty()) {
        val refresh = obj["refresh_token"]?.jsonPrimitive?.contentOrNull
        return RefreshOutcome.Success(access, refresh)
    }
    val error = obj["error"]?.jsonPrimitive?.contentOrNull
    return if (error == "invalid_grant") RefreshOutcome.InvalidGrant
           else RefreshOutcome.TransientFailure
}


private fun urlParam(name: String): String? =
    URLSearchParams(window.location.search).get(name)

private fun emptyJsObject(): dynamic = js("({})")
